import { IEntityBase } from 'app/models/responses/IEntityBase';
import { IODataResponse } from 'app/models/responses/IODataResponse';
import { rootConfig } from 'config';
import { useSnackbar } from 'notistack';
import type { QueryOptions } from 'odata-query';
import buildQuery from 'odata-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'utils/axios';
import { useDeepCompareMemoize } from './useDeepCompareMemoize';
import { useInterval } from './useInterval';
import { PagingOptions, usePaging } from './usePaging';

interface InternalPagingOptions {
  enabled: boolean;
  initialPageSize?: number;
}

interface ODataQueryOptions<TEntity extends IEntityBase> {
  query?: Partial<QueryOptions<TEntity>>;
  resource: string;
  paging?: InternalPagingOptions;
  pollInterval?: number;
  pause?: boolean;
  delay?: number;
  method?: 'get' | 'post' | 'patch' | 'put' | 'delete';
  requestData?: any;
}

export interface ODataQueryResult<TResponse extends any = any> {
  execute: () => Promise<void>;
  setData: (data: TResponse) => void;
  data?: TResponse;
  currentPage: number;
  setCurrentPage: (currentPage: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  rowCount: number;
  loading: boolean;
}

export const useODataQuery = <
  TResponse extends any = any,
  TEntity extends IEntityBase = IEntityBase,
>(
  options: ODataQueryOptions<TEntity>,
) => {
  const {
    paging: providedPaging,
    pollInterval,
    resource: endpoint,
    pause,
    query: providedQuery,
    delay,
    method = 'get',
    requestData: providedRequestData,
  } = options;
  const query = useDeepCompareMemoize(providedQuery);
  const requestData = useDeepCompareMemoize(providedRequestData);
  const paging = useDeepCompareMemoize(providedPaging);

  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<TResponse>();

  const pagingOptions = useMemo<PagingOptions>(() => {
    let rowCount = 0;
    if (paging?.enabled && data) {
      try {
        rowCount = (data as IODataResponse<any>)['@odata.count'] ?? rowCount;
      } catch {
      } finally {
        return { rowCount, initialPageSize: paging?.initialPageSize };
      }
    }

    return { rowCount, initialPageSize: paging?.initialPageSize };
  }, [paging, data]);
  const { rowCount, setPageSize, setCurrentPage, currentPage, pageSize } = usePaging(pagingOptions);

  const execute = async () => {
    await executeInternal(true, true, false);
  };

  const executeInternal = useCallback(
    async (indicateLoading = true, honorDelay = true, honorPause = true) => {
      if (honorPause && pause) return;

      try {
        setLoading(indicateLoading);
        if (honorDelay) await new Promise((resolve) => setTimeout(resolve, delay ?? 250));
        const effectiveQuery = { ...query };
        if (paging) {
          effectiveQuery.top = pageSize;
          effectiveQuery.skip = pageSize * currentPage;
          effectiveQuery.count = true;
        }

        const queryString = buildQuery(effectiveQuery);
        const response = await axios.request<TResponse>({
          url: `${rootConfig.odataRoute}/${endpoint}${queryString}`,
          method,
          data: requestData,
        });
        setData(response.data);
      } catch (error) {
        if (!pollInterval)
          enqueueSnackbar('Something went wrong. Please try again later.', { variant: 'error' });
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [
      endpoint,
      query,
      pageSize,
      currentPage,
      paging,
      pause,
      delay,
      method,
      requestData,
      enqueueSnackbar,
    ],
  );

  useInterval(() => executeInternal(false, false), pollInterval ?? null);

  useEffect(() => {
    (async () => {
      await executeInternal();
    })();
  }, [executeInternal]);

  const result: ODataQueryResult<TResponse> = {
    data,
    setData,
    execute,
    rowCount,
    setPageSize,
    setCurrentPage,
    currentPage,
    pageSize,
    loading,
  };

  return result;
};
